import { createRouter, createWebHistory, type RouteRecordRaw } from "vue-router";

import { useSessionStore } from "@/entities/session";
import { LoginPage } from "@/pages/login";
import { AppLayout } from "@/widgets";

import { createAuthGuard } from "./guard";

/**
 * Типобезпечний `meta` (ADR-0006 D5): `requiresAuth` керує guard'ом,
 * `layout: 'auth'` маркує layout-free публічні роути (наразі — `/login`).
 */
declare module "vue-router" {
  interface RouteMeta {
    requiresAuth?: boolean;
    layout?: "auth";
  }
}

/**
 * Роутер адмінки (ADR-0003 D2/D6, history-mode).
 *
 * `/login` — eager: перший екран аноніма, критичний шлях до форми входу.
 * Всі інші сторінки — lazy (`() => import()`): окремий чанк на маршрут.
 * AppLayout — eager: shell, завжди потрібен автентифікованим користувачам.
 *
 * Конвенція: нові сторінки додавати ТІЛЬКИ через `() => import("@/pages/<slice>")`.
 * Barrel `@/pages` не використовувати в роутері (тягне всі сторінки в initial bundle).
 */
const routes: RouteRecordRaw[] = [
  {
    path: "/login",
    name: "login",
    component: LoginPage,
    meta: { requiresAuth: false, layout: "auth" },
  },
  {
    path: "/signup",
    name: "signup",
    component: () => import("@/pages/signup").then((m) => m.SignupPage),
    meta: { requiresAuth: false, layout: "auth" },
  },
  {
    path: "/forgot-password",
    name: "forgot-password",
    component: () =>
      import("@/pages/forgot-password").then((m) => m.ForgotPasswordPage),
    meta: { requiresAuth: false, layout: "auth" },
  },
  {
    path: "/",
    component: AppLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: "",
        name: "home",
        component: () => import("@/pages/home").then((m) => m.HomePage),
      },
      {
        path: "clients",
        name: "clients",
        component: () =>
          import("@/pages/clients").then((m) => m.ClientsListPage),
      },
      {
        path: "clients/:id",
        name: "client-detail",
        component: () =>
          import("@/pages/clients").then((m) => m.ClientDetailPage),
        props: true,
      },
      {
        path: "summary",
        name: "summary",
        component: () => import("@/pages/summary").then((m) => m.SummaryPage),
      },
      // Майстри — master-detail (ADR-0013 D2). Статичний `masters/new` перелічено
      // перед динамічним `masters/:id` для читабельності (vue-router і так ранжує
      // статичні сегменти вище). create/profile — back-link-заглушки (інкремент A);
      // форму додає write-path (B).
      {
        path: "masters",
        name: "masters",
        component: () =>
          import("@/pages/masters").then((m) => m.MastersListPage),
      },
      {
        path: "masters/new",
        name: "master-create",
        component: () =>
          import("@/pages/masters").then((m) => m.MasterCreatePage),
      },
      {
        path: "masters/:id",
        name: "master-profile",
        component: () =>
          import("@/pages/masters").then((m) => m.MasterProfilePage),
        props: true,
      },
      {
        path: "services",
        name: "services",
        component: () =>
          import("@/pages/services").then((m) => m.ServicesPage),
      },
      {
        path: "salon-profile",
        name: "salon-profile",
        component: () =>
          import("@/pages/salon-profile").then((m) => m.SalonProfilePage),
      },
      {
        path: "schedule",
        name: "schedule",
        component: () =>
          import("@/pages/schedule").then((m) => m.SchedulePage),
      },
    ],
  },
  {
    path: "/:pathMatch(.*)*",
    redirect: "/",
  },
];

// DEV-only вітрина design-system (ADR-0007 D1). Статичний `import.meta.env.DEV`
// → Vite tree-shake-ить гілку у prod-білді: роуту немає, нуль ваги в бандлі.
// БЕЗ requiresAuth — лишається публічним dev-tool.
if (import.meta.env.DEV) {
  routes.splice(routes.length - 1, 0, {
    path: "/_styleguide",
    name: "styleguide",
    component: () => import("@/pages/styleguide").then((m) => m.StyleguidePage),
  });
}

export function createAppRouter(): ReturnType<typeof createRouter> {
  const router = createRouter({
    history: createWebHistory(),
    routes,
  });

  // Guard поверх session-стору (ADR-0006 D5′, Amendment A1). Якщо hint є —
  // isBootstrapped=true, guard вирішує синхронно. Cold-path: awaits single-flight
  // promise з whenBootstrapped() (той самий, що стартував main.ts) — без другого запиту.
  router.beforeEach(async (to) => {
    const session = useSessionStore();
    if (!session.isBootstrapped) {
      await session.whenBootstrapped();
    }
    return createAuthGuard(session)(to);
  });

  return router;
}
